import { acceptInviteSchema } from "@frontend-shared/schema/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAcceptInvite } from "@/api/auth/useAcceptInvite";
import { useGetInviteDetails } from "@/api/auth/useGetInviteDetails";
import { FormSubmitButton } from "@/components/FormSubmitButton";
import { Logo } from "@/components/Logo";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import {
  ESpinningLoaderSize,
  SpinningLoader,
} from "@/components/SpinningLoader";
import { StyledInput } from "@/components/StyledInput";
import { StyledPasswordInput } from "@/components/StyledPasswordInput";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PATHS } from "@/constants/paths";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { stripUrlCredential } from "@/lib/strip-url-credential";
import { cn } from "@/lib/utils";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

import { CenterCardLayout } from "./CenterCardLayout";
import { AUTH_CONTROL_HEIGHT_CLASS } from "./constants/auth-controls";

const AcceptInvite = () => {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("page_title"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // NOTE: NSAI-6105 — read ONCE, before the effect below removes it from the
  // URL. Reading `searchParams` on every render instead would blank the token
  // the moment the address bar is scrubbed, dropping the user out of the form
  // and re-firing the invite-details query with a null token.
  const [token] = useState(() => searchParams.get("token"));

  useEffect(() => {
    stripUrlCredential({ parameter: "token" });
  }, []);

  const {
    data: inviteDetails,
    isLoading,
    isError,
  } = useGetInviteDetails(token);
  const acceptInviteMutation = useAcceptInvite();

  const form = useForm({
    ...FORM_VALIDATION_OPTIONS,
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    },
    resolver: zodResolver(acceptInviteSchema(t)),
  });

  const handleSubmit = form.handleSubmit((data) => {
    /* istanbul ignore next */
    if (!token) return;

    acceptInviteMutation.mutate(
      {
        token,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      },
      {
        onSuccess: () => navigate(PATHS.CHAT, { replace: true }),
      },
    );
  });

  // NOTE: NSAI-6105 — no early `NotFound` for a missing token any more. The
  // effect above strips the credential from the URL, so a reload or a Back onto
  // this entry legitimately arrives without one, and a bare 404 reads as a bug.
  // The query is disabled without a token, so `inviteDetails` stays undefined
  // and the invalid state below renders — the same copy an expired invite gets,
  // which is what this is from the user's side.
  if (isLoading) {
    return (
      <CenterCardLayout>
        <div className="flex justify-center py-10">
          <SpinningLoader size={ESpinningLoaderSize.MEDIUM} />
        </div>
      </CenterCardLayout>
    );
  }

  if (isError || !inviteDetails) {
    return (
      <CenterCardLayout>
        <div
          className="flex flex-col items-center gap-3 text-center"
          data-testid="accept-invite_invalid"
        >
          <Logo
            wrapperClassName="flex justify-center"
            className="h-logo-height-auth w-auto"
          />
          <h1 className="text-xl md:text-2xl font-semibold mt-3">
            {t("accept_invite.invalid_title")}
          </h1>
          <p className="text-foreground-secondary text-balance">
            {t("accept_invite.invalid_description")}
          </p>
        </div>
      </CenterCardLayout>
    );
  }

  // NOTE: When the org requires SSO the accept-link path is closed — direct the
  // invitee to sign in through SSO instead of showing the set-password form.
  if (inviteDetails.requiresSso) {
    return (
      <CenterCardLayout>
        <div
          className="flex flex-col items-center gap-3 text-center"
          data-testid="accept-invite_requires-sso"
        >
          <Logo
            wrapperClassName="flex justify-center"
            className="h-logo-height-auth w-auto"
          />
          <h1 className="text-xl md:text-2xl font-semibold mt-3">
            {t("accept_invite.sso_title")}
          </h1>
          <p className="text-foreground-secondary text-balance">
            {t("accept_invite.sso_description", {
              organizationName: inviteDetails.organizationName,
            })}
          </p>
          <Button
            className={cn("w-full mt-2", AUTH_CONTROL_HEIGHT_CLASS)}
            onClick={() => navigate(PATHS.AUTH.SSO, { replace: true })}
            data-testid="accept-invite_sso-button"
          >
            {t("accept_invite.sso_button")}
          </Button>
        </div>
      </CenterCardLayout>
    );
  }

  return (
    <CenterCardLayout>
      <div className="flex flex-col justify-center">
        <Logo
          wrapperClassName="flex justify-center"
          className="h-logo-height-auth w-auto"
        />
        <h1
          className="text-xl md:text-2xl lg:text-3xl font-semibold text-center mt-3 md:mt-5"
          data-testid="accept-invite_title"
        >
          <Trans
            t={t}
            i18nKey="accept_invite.title"
            values={{ organizationName: inviteDetails.organizationName }}
            components={{
              Highlighted: <span className="text-highlighted" />,
            }}
          />
        </h1>
        <p
          className="text-center text-foreground-secondary mt-2 md:mt-3 text-balance"
          data-testid="accept-invite_description"
        >
          {t("accept_invite.description", { email: inviteDetails.email })}
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <div className="flex gap-3">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="flex-1">
                  <FormLabel>{t("first_name.label")}</FormLabel>
                  <FormControl>
                    <StyledInput
                      className={AUTH_CONTROL_HEIGHT_CLASS}
                      placeholder={t("first_name.placeholder")}
                      {...field}
                      isError={!!error}
                      data-testid="accept-invite_first-name-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="flex-1">
                  <FormLabel>{t("last_name.label")}</FormLabel>
                  <FormControl>
                    <StyledInput
                      className={AUTH_CONTROL_HEIGHT_CLASS}
                      placeholder={t("last_name.placeholder")}
                      {...field}
                      isError={!!error}
                      data-testid="accept-invite_last-name-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="password"
            rules={{ deps: ["confirmPassword"] }}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>{t("password.label")}</FormLabel>
                <FormControl>
                  <StyledPasswordInput
                    className={AUTH_CONTROL_HEIGHT_CLASS}
                    placeholder={t("password.placeholder")}
                    {...field}
                    autoComplete="new-password"
                    isError={!!error}
                    data-testid="accept-invite_password-input"
                  />
                </FormControl>
                <PasswordStrengthMeter
                  password={field.value}
                  data-testid="accept-invite_password-strength"
                />
                <FormMessage data-testid="accept-invite_password-error" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>{t("confirm_password.label")}</FormLabel>
                <FormControl>
                  <StyledPasswordInput
                    className={AUTH_CONTROL_HEIGHT_CLASS}
                    placeholder={t("confirm_password.placeholder")}
                    {...field}
                    autoComplete="new-password"
                    isError={!!error}
                    data-testid="accept-invite_confirm-password-input"
                  />
                </FormControl>
                <FormMessage data-testid="accept-invite_confirm-password-error" />
              </FormItem>
            )}
          />
          <FormSubmitButton
            className={cn("w-full", AUTH_CONTROL_HEIGHT_CLASS)}
            isLoading={acceptInviteMutation.isPending}
            data-testid="accept-invite_submit-button"
          >
            {t("accept_invite.submit")}
          </FormSubmitButton>
        </form>
      </Form>
    </CenterCardLayout>
  );
};

export default memo(AcceptInvite);
